import React, { useState, useContext, createContext, useEffect, FunctionComponent } from "react"
import dayjs from "dayjs"
import { courseLanguages, languages, courseLevels, courseTrainings, rolesUser } from "@murphy-frontend/web-core/constants"
import { useInjection } from "../../../../common/contexts/InversifyContext"
import PersistenceType, { IPersistenceService, LocalStorageKeys } from "../../../../common/interfaces/IPersistenceService"
import ApiServiceType, { IApiService } from "../../../../common/interfaces/IApiService"

import { TimeZoneType } from "@murphy-frontend/common/services/TimeService"

export type LanguageType = "swe" | "eng" | "nor" | "fin" | "dan"
export type CourseLanguageType = "swe" | "eng"
export type CourseLevelType = 1 | 2 | 3
export type CourseTrainingType = 1 | 2
export type SelectedCourseLanguagesType = CourseLanguageType[]
export type SelectedCourseLevelsType = CourseLevelType[]
export type SelectedUserCourseLevelsType = CourseLevelType[]
export type SelectedCourseTrainingsType = CourseTrainingType[]
export type CollapsedType = boolean
export type RoleType = 1 | 2
export type SelectedRoleType = RoleType[]
export type EducationSelectedRoleType = RoleType[]
export type IsMobileAppType = boolean
export type ShowNonZeroType = boolean

interface UserPreferencesContextType {
	translations: Record<string, string>
	timeZone: TimeZoneType
	language: LanguageType
	collapsed: CollapsedType
	selectedCourseLanguages: SelectedCourseLanguagesType
	selectedCourseLevels: SelectedCourseLevelsType
	selectedUserCourseLevels: SelectedUserCourseLevelsType
	selectedCourseTrainings: SelectedCourseTrainingsType
	courseFilter: string
	selectedRole: SelectedRoleType
	educationSelectedRole: EducationSelectedRoleType
	isMobileApp: IsMobileAppType
	showNonZero: ShowNonZeroType
	setTimeZone: (tz: TimeZoneType) => void
	setLanguage: (lang: LanguageType) => void
	setSelectedCourseLanguages: React.Dispatch<React.SetStateAction<SelectedCourseLanguagesType>>
	setSelectedCourseLevels: React.Dispatch<React.SetStateAction<SelectedCourseLevelsType>>
	setSelectedUserCourseLevels: React.Dispatch<React.SetStateAction<SelectedUserCourseLevelsType>>
	setSelectedCourseTrainings: React.Dispatch<React.SetStateAction<SelectedCourseTrainingsType>>
	setCollapsed: (collapsed: CollapsedType) => void
	updateCourseFilter: (courseFilter: string) => void
	setSelectedRole: React.Dispatch<React.SetStateAction<SelectedRoleType>>
	setEducationSelectedRole: React.Dispatch<React.SetStateAction<EducationSelectedRoleType>>
	setIsMobileApp: React.Dispatch<React.SetStateAction<IsMobileAppType>>
	setShowNonZero: React.Dispatch<React.SetStateAction<ShowNonZeroType>>
}

interface UserPreferencesContextProps {
	children?: React.ReactNode
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined)
const defaultLang = "eng" // Swedish default
const defaultTimeZone = "1" // Local time default

const UserPreferencesProvider: FunctionComponent<UserPreferencesContextProps> = ({ children }) => {
	const [translations, setTranslations] = useState<any>("")
	const [collapsed, setCollapsed] = useState<CollapsedType>(null)
	const [timeZone, setTimeZone] = useState<TimeZoneType>(null)
	const [language, setLanguage] = useState<LanguageType>(null)
	const [selectedCourseLanguages, setSelectedCourseLanguages] = useState<SelectedCourseLanguagesType>(null)
	const [selectedCourseLevels, setSelectedCourseLevels] = useState<SelectedCourseLevelsType>(null)
	const [selectedUserCourseLevels, setSelectedUserCourseLevels] = useState<SelectedUserCourseLevelsType>(null)
	const [selectedCourseTrainings, setSelectedCourseTrainings] = useState<SelectedCourseTrainingsType>(null)
	const [courseFilter, setCourseFilter] = useState<string>(null)
	const [selectedRole, setSelectedRole] = useState<SelectedRoleType>(null)
	const [educationSelectedRole, setEducationSelectedRole] = useState<EducationSelectedRoleType>(null)
	const [isMobileApp, setIsMobileApp] = useState<IsMobileAppType>(null)
	const [showNonZero, setShowNonZero] = useState<ShowNonZeroType>(null)
	const persistenceService = useInjection<IPersistenceService>(PersistenceType.IPersistenceService)
	const apiService = useInjection<IApiService>(ApiServiceType.IApiService)

	const getTranslationsFromApi = (lang: string) => {
		apiService.getLangTranslations(lang).then(response => {
			const { data } = response
			const stringifiedTranslations = JSON.stringify(data)
			persistenceService.setTranslations(stringifiedTranslations)
			setTranslations(data)
		})
	}

	const setLocale = (lang: string) => {
		let locale = "sv"
		switch (lang) {
			case "swe":
				locale = "sv"
				break
			case "eng":
				locale = "en"
				break
			case "nor":
				locale = "nb"
				break
			case "fin":
				locale = "fi"
				break
			default:
				locale = "sv"
		}

		dayjs.locale(locale)
	}

	useEffect(() => {
		if (timeZone) {
			persistenceService.setSelectedTimeZone(timeZone)
		}
	}, [timeZone])

	useEffect(() => {
		if (language) {
			persistenceService.setLanguage(language)
			getTranslationsFromApi(language)
			setLocale(language)
		}
	}, [language])

	useEffect(() => {
		if (selectedRole) {
			const jsonVal = JSON.stringify(selectedRole)
			persistenceService.setSelectedRole(jsonVal)
		}
	}, [selectedRole])

	useEffect(() => {
		if (educationSelectedRole) {
			const jsonVal = JSON.stringify(educationSelectedRole)
			persistenceService.setEducationSelectedRole(jsonVal)
		}
	}, [educationSelectedRole])

	useEffect(() => {
		if (isMobileApp != null) {
			persistenceService.setIsMobileApp(isMobileApp)
		}
	}, [isMobileApp])

	useEffect(() => {
		if (showNonZero != null) {
			persistenceService.setShowNonZero(showNonZero)
		}
	}, [showNonZero])

	useEffect(() => {
		if (collapsed != null) {
			persistenceService.setCollapsed(collapsed)
		}
	}, [collapsed])

	useEffect(() => {
		if (selectedCourseLanguages) {
			const jsonVal = JSON.stringify(selectedCourseLanguages)
			persistenceService.setSelectedCourseLanguages(jsonVal)
		}
	}, [selectedCourseLanguages])

	useEffect(() => {
		if (selectedCourseLevels) {
			const jsonVal = JSON.stringify(selectedCourseLevels)
			persistenceService.setSelectedCourseLevels(jsonVal)
		}
	}, [selectedCourseLevels])

	useEffect(() => {
		if (selectedUserCourseLevels) {
			const jsonVal = JSON.stringify(selectedUserCourseLevels)
			persistenceService.setSelectedUserCourseLevels(jsonVal)
		}
	}, [selectedUserCourseLevels])

	useEffect(() => {
		if (selectedCourseTrainings) {
			const jsonVal = JSON.stringify(selectedCourseTrainings)
			persistenceService.setSelectedCourseTrainings(jsonVal)
		}
	}, [selectedCourseTrainings])

	useEffect(() => {
		if (courseFilter) {
			persistenceService.setCourseFilter(courseFilter)
		} else {
			setCourseFilter(persistenceService.getCourseFilter())
		}
	}, [courseFilter])

	const updateLanguage = () => {
		const lang = persistenceService.getLanguage()
		let currentLang = languages.filter(p => p.id == defaultLang)[0].id
		if (lang) {
			currentLang = languages.filter(p => p.id == lang)[0].id
			setLanguage(currentLang)
		} else {
			setLanguage(currentLang)
		}
	}

	const updateTimeZone = () => {
		const tz = persistenceService.getSelectedTimeZone()
		if (tz) {
			setTimeZone(tz as TimeZoneType)
		} else {
			setTimeZone(defaultTimeZone)
		}
	}

	const updateCollapsed = () => {
		const col = persistenceService.getCollapsed()
		setCollapsed(col)
	}

	const updateIsMobileApp = () => {
		const col = persistenceService.getIsMobileApp()
		setIsMobileApp(col)
	}

	const updateShowNonZero = () => {
		const col = persistenceService.getShowNonZero()
		setShowNonZero(col)
	}

	const updateEducationSelectedRole = () => {
		const val = persistenceService.getEducationSelectedRole()
		if (val && val.length > 0) {
			const parsedVal = JSON.parse(val)
			setEducationSelectedRole(parsedVal as EducationSelectedRoleType)
		} else {
			setEducationSelectedRole(rolesUser.map(p => p.id))
		}
	}

	const updateSelectedRole = () => {
		const val = persistenceService.getSelectedRole()
		if (val && val.length > 0) {
			const parsedVal = JSON.parse(val)
			setSelectedRole(parsedVal as SelectedRoleType)
		} else {
			setSelectedRole(rolesUser.map(p => p.id))
		}
	}

	const updateSelectedCourseLanguages = () => {
		const val = persistenceService.getSelectedCourseLanguages()
		if (val && val.length > 0) {
			const parsedVal = JSON.parse(val)
			setSelectedCourseLanguages(parsedVal as SelectedCourseLanguagesType)
		} else {
			setSelectedCourseLanguages(courseLanguages.map(p => p.id))
		}
	}

	const updateSelectedCourseLevels = () => {
		const val = persistenceService.getSelectedCourseLevels()
		if (val && val.length > 0) {
			const parsedVal = JSON.parse(val)
			setSelectedCourseLevels(parsedVal as SelectedCourseLevelsType)
		} else {
			setSelectedCourseLevels(courseLevels.map(p => p.id))
		}
	}

	const updateSelectedUserCourseLevels = () => {
		const val = persistenceService.getSelectedUserCourseLevels()
		if (val && val.length > 0) {
			const parsedVal = JSON.parse(val)
			setSelectedUserCourseLevels(parsedVal as SelectedUserCourseLevelsType)
		} else {
			setSelectedUserCourseLevels(courseLevels.map(p => p.id))
		}
	}

	const updateSelectedCourseTrainings = () => {
		const val = persistenceService.getSelectedCourseTrainings()
		if (val && val.length > 0) {
			const parsedVal = JSON.parse(val)
			setSelectedCourseTrainings(parsedVal as SelectedCourseTrainingsType)
		} else {
			setSelectedCourseTrainings(courseTrainings.map(p => p.id))
		}
	}

	const updateCourseFilter = (courseFilter?: string) => {
		if (courseFilter) {
			setCourseFilter(courseFilter)
		}
	}

	useEffect(() => {
		updateTimeZone()
		updateLanguage()
		updateSelectedCourseLanguages()
		updateSelectedCourseLevels()
		updateSelectedUserCourseLevels()
		updateSelectedCourseTrainings()
		updateCollapsed()
		updateSelectedRole()
		updateEducationSelectedRole()
		updateIsMobileApp()
		updateShowNonZero()
		function storageEventHandler(event: any) {
			if (event.key === LocalStorageKeys.Language) {
				updateLanguage()
			}
			if (event.key === LocalStorageKeys.SelectedTimeZone) {
				updateTimeZone()
			}
			if (event.key === LocalStorageKeys.SelectedCourseLanguages) {
				updateSelectedCourseLanguages()
			}
			if (event.key === LocalStorageKeys.SelectedCourseLevels) {
				updateSelectedCourseLevels()
			}
			if (event.key === LocalStorageKeys.SelectedUserCourseLevels) {
				updateSelectedUserCourseLevels()
			}
			if (event.key === LocalStorageKeys.SelectedCourseTrainings) {
				updateSelectedCourseTrainings()
			}
			if (event.key === LocalStorageKeys.Collapsed) {
				updateCollapsed()
			}
			if (event.key === LocalStorageKeys.IsMobileApp) {
				updateIsMobileApp()
			}
			if (event.key === LocalStorageKeys.ShowNonZero) {
				updateShowNonZero()
			}
			if (event.key === LocalStorageKeys.SelectedRole) {
				updateSelectedRole()
			}
			if (event.key === LocalStorageKeys.EducationSelectedRole) {
				updateEducationSelectedRole()
			}
		}
		// Hook up the event handler
		window.addEventListener("storage", storageEventHandler)
		return () => {
			// Remove the handler when the component unmounts
			window.removeEventListener("storage", storageEventHandler)
		}
	}, [])

	const value = {
		translations,
		timeZone,
		language,
		courseFilter,
		selectedCourseLanguages,
		selectedCourseLevels,
		selectedUserCourseLevels,
		selectedCourseTrainings,
		selectedRole,
		educationSelectedRole,
		collapsed,
		isMobileApp,
		showNonZero,
		setTimeZone,
		setLanguage,
		setSelectedCourseLanguages,
		setSelectedCourseLevels,
		setSelectedUserCourseLevels,
		setSelectedCourseTrainings,
		updateCourseFilter,
		setCollapsed,
		setIsMobileApp,
		setShowNonZero,
		setSelectedRole,
		setEducationSelectedRole
	}

	return <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
}

export default UserPreferencesProvider

export const useUserPreferences = () => useContext(UserPreferencesContext)
